import { Environment } from "../environment";
import * as SentryTypes from "@sentry/types";

export type LogLevel = keyof typeof SentryTypes.Severity;

export class Logger {
  private get Sentry() {
    return Environment.Sentry;
  }

  public captureException = (
    exc: Error,
    level: LogLevel = "Error",
    callback?: (scope: SentryTypes.Scope) => void
  ) => {
    if (__DEV__) {
      console.groupCollapsed("Sentry exception");
      console.warn(exc);
      console.groupEnd();
    }

    // Capture exception with sentry
    this.Sentry?.withScope((scope) => {
      scope.setLevel(level.toLowerCase() as SentryTypes.Severity);
      callback?.(scope);
      this.Sentry?.captureException(exc);
    });
  };

  public captureMessage = (
    message: string,
    level: LogLevel = "Log",
    callback?: (scope: SentryTypes.Scope) => void
  ) => {
    if (__DEV__) {
      console.groupCollapsed("Sentry warning");
      console.warn(message);
      console.groupEnd();
    }

    // Capture message with sentry
    this.Sentry?.withScope((scope) => {
      scope.setLevel(level.toLowerCase() as SentryTypes.Severity);
      callback?.(scope);
      this.Sentry?.captureMessage(message);
    });
  };

  public captureObject(
    title: string,
    obj: any,
    level: LogLevel = "Log",
    callback?: (scope: SentryTypes.Scope) => void
  ) {
    // Capture message with sentry

    if (__DEV__) {
      console.groupCollapsed(`Sentry object capture: ${title}`);
      console.log(obj);
      console.groupEnd();
    }

    this.Sentry?.withScope((scope) => {
      scope.setLevel(level.toLowerCase() as SentryTypes.Severity);
      scope.setExtra("data", obj);
      callback?.(scope);
      this.Sentry?.captureMessage(title);
    });
  }

  public capture = (
    event: Error | string,
    level?: LogLevel,
    callback?: (scope: SentryTypes.Scope) => void
  ) => {
    if (event instanceof Error) {
      this.captureException(event, level, callback);
    } else {
      this.captureMessage(event, level, callback);
    }
  };

  public debug(log: string) {
    this.capture(log, "Debug");
  }

  public info(log: string) {
    this.capture(log, "Info");
  }

  public warn(log: string) {
    this.capture(log, "Warning");
  }

  public error(log: string) {
    this.capture(log, "Error");
  }

  public dev(message: any, group = "generic") {
    if (__DEV__) {
      if (typeof message === "string") {
        console.log(message);
      } else {
        console.groupCollapsed(`Dev object capture: ${group}`);
        console.log(message);
        console.groupEnd();
      }
    }
  }
}

export const DefaultLogger = new Logger();
